import React from 'react';

import { uniq } from 'lodash-es';
import * as Yup from 'yup';

import MyPagePanel from '@/components/ui/box/MyPagePanel';
import Paper from '@/components/ui/box/Paper';
import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Label from '@/components/ui/forms/Label';
import PasswordInput from '@/components/ui/forms/PasswordInput';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import useCurrentUser from '@/hooks/store/useCurrentUser';
import {
  type ParentPasswordChangeFormValues,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEXP,
} from '@/interfaces/parent';

import styles from './ParentPasswordChangeForm.module.scss';

interface Props {
  onSubmit: (values: ParentPasswordChangeFormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
  serverError?: string[];
}

const ParentPasswordChangeForm: React.FC<Props> = ({
  onSubmit,
  onCancel,
  isLoading = false,
  serverError = [],
}) => {
  const [currentParent] = useCurrentUser();

  const email = currentParent?.email;

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .trim()
      .required('パスワードは必須です')
      // NOTE: .min()にはexcludeEmptyStringオプションがないため、.matches()を使用
      .matches(/.{6,}/, {
        excludeEmptyString: true,
        message: `パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください`,
      })
      .max(
        PASSWORD_MAX_LENGTH,
        `パスワードは${PASSWORD_MAX_LENGTH}文字以内で入力してください`,
      )
      .matches(PASSWORD_REGEXP, {
        excludeEmptyString: true,
        message:
          'パスワードは半角アルファベットと半角数字をそれぞれ1文字以上で入力してください',
      })
      .notOneOf(
        [email],
        'パスワードとメールアドレスに同じ文字列は使用できません',
      ),
    password_confirmation: Yup.string()
      .trim()
      .required('パスワード（確認用）は必須です')
      .oneOf([Yup.ref('password'), ''], 'パスワードが一致しません'),
  });

  const {
    registerWithInnerRef,
    handleSubmit,
    reset,
    formState: { isSubmitting, isValid, errorMessages },
  } = useReactHookForm<ParentPasswordChangeFormValues>({
    validationSchema,
    defaultValues: {
      password: '',
      password_confirmation: '',
    },
  });

  const onHandleSubmit = (values: ParentPasswordChangeFormValues) => {
    void onSubmit(values);
    reset();
  };

  const passwordErrors = uniq([
    ...(errorMessages.password || []),
    ...(errorMessages.password_confirmation || []),
  ]);
  const serverErrorMessages = [...(serverError || [])];

  const buttonDisabled = !isValid || isSubmitting || isLoading;

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onHandleSubmit)}
      className={styles.ParentPasswordChangeForm}
    >
      <Paper>
        <MyPagePanel title="パスワードの変更">
          <FormGroup>
            <Label>新しいパスワード</Label>
            <Field className={styles.PasswordInput} isFullWidth>
              <PasswordInput
                {...registerWithInnerRef('password')}
                disabled={isSubmitting}
                autoComplete="new-password"
                placeholder="新しいパスワードを入力"
              />
            </Field>

            <ul className={styles.Comment}>
              <li>
                パスワードは最低6文字で、半角アルファベットと半角数字をそれぞれ1文字以上入れてください。
              </li>
              <li>
                名前＋生年月日などは推測されやすいため、英数以外の記号を組み合わせるなど推測されにくいものの設定を推奨します。
              </li>
            </ul>

            <Field className={styles.PasswordInput} isFullWidth>
              <PasswordInput
                {...registerWithInnerRef('password_confirmation')}
                disabled={isSubmitting}
                autoComplete="new-password"
                placeholder="新しいパスワードを入力（確認用）"
              />
            </Field>
            <ErrorList className={styles.ErrorList} errors={passwordErrors} />
            <ErrorList
              className={styles.ErrorList}
              errors={serverErrorMessages}
            />
          </FormGroup>
        </MyPagePanel>
      </Paper>
      <div className={styles.ButtonArea}>
        <Button
          color="primary"
          type="submit"
          disabled={buttonDisabled}
          className={styles.Button}
        >
          保存
        </Button>
        <Button
          color="link"
          onClick={onCancel}
          className={`${styles.Button} ${styles.CancelButton}`}
        >
          キャンセル
        </Button>
      </div>
    </form>
  );
};

export default ParentPasswordChangeForm;
